import React from 'react';
import { Link } from 'react-router-dom';
import {
    FaBuilding,
    FaUsers,
    FaCalendarCheck,
    FaFileInvoiceDollar,
    FaShieldAlt,
    FaFileExport,
    FaEnvelope,
    FaPhoneAlt,
    FaMapMarkerAlt,
    FaArrowRight
} from 'react-icons/fa';
import Navbar from '../Common/Navbar'; // adjust path if your Navbar file lives elsewhere
import Footer from '../Common/Footer';
import { APP_CONFIG } from '../../config/app.config';
import OrientalPayLogo from '../../Assets/oriental-pay-logo.png';
import './OrientalPay.css';

const features = [
    {
        icon: <FaBuilding size={20} />,
        title: 'Organization Management',
        desc: 'Manage multiple companies and organizations from a single platform.'
    },
    {
        icon: <FaUsers size={20} />,
        title: 'Employee Management',
        desc: 'Maintain complete employee and payroll data in one secure place.'
    },
    {
        icon: <FaCalendarCheck size={20} />,
        title: 'Flexible Payroll Runs',
        desc: 'Process weekly, biweekly, or monthly payroll with ease.'
    },
    {
        icon: <FaFileInvoiceDollar size={20} />,
        title: 'Automatic Tax Calculations',
        desc: 'Ontario-specific CPP, EI, and federal/provincial income tax, calculated automatically.'
    },
    {
        icon: <FaFileExport size={20} />,
        title: 'Paystubs & CRA T4 Export',
        desc: 'Generate paystubs and export CRA-compatible T4 XML files for reporting.'
    },
    {
        icon: <FaShieldAlt size={20} />,
        title: 'Secure Access',
        desc: 'Secure user access powered by Microsoft Entra ID / MSAL.'
    }
];

const steps = [
    { n: '01', title: 'Set up your organization', desc: 'Add your company and its employees in minutes.' },
    { n: '02', title: 'Run payroll', desc: 'Process weekly, biweekly, or monthly, on your schedule.' },
    { n: '03', title: 'Taxes, calculated', desc: 'CPP, EI, and income tax are worked out automatically.' },
    { n: '04', title: 'Paystubs & T4s', desc: 'Generate paystubs and export CRA-ready T4 XML files.' },
];

const OrientalPay = () => {
    return (
        <>
            <div className="oriental_pay_container">
                <div className="oriental_pay_bg">
                    <Navbar />
                    <div className="oriental_pay_head_container">
                        <div className="op-eyebrow mb-3">Payroll SaaS, built for Ontario</div>
                        <h1>OrientalPay</h1>
                        <p className="oriental_pay_head_subtitle mb-4">
                            A multi-tenant payroll platform for Ontario businesses — manage organizations
                            and employees, run payroll, and calculate taxes automatically.
                        </p>
                        <a 
                            href="https://orientalpay.ca/" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn op-btn-gold d-inline-flex align-items-center"
                            style={{ padding: "0.75rem 2rem", fontSize: "1.1rem", fontWeight: "600" }}
                        >
                            Visit OrientalPay.ca <FaArrowRight className="ms-2" size={16} />
                        </a>
                    </div>
                </div>
            </div>

            <div className="op-page">
                {/* Intro Section */}
                <div className="op-section pb-0">
                    <div className="container">
                        <div className="op-intro-card p-4 p-md-5 mx-auto text-center">
                            <img src={OrientalPayLogo} alt="OrientalPay - Efficient Payroll Solutions" className="op-logo mb-4" />
                            <p className="text-muted mx-auto mb-4" style={{ maxWidth: '640px', fontSize: '0.98rem' }}>
                                A multi-tenant payroll platform for Ontario businesses — manage organizations
                                and employees, run payroll, calculate Ontario-specific taxes automatically,
                                generate paystubs, and export CRA T4 XML files, all in one secure place.
                            </p>
                            <div className="d-flex gap-3 justify-content-center flex-wrap">
                                <Link to="/contact" className="btn op-btn-gold d-inline-flex align-items-center">
                                    Talk to us <FaArrowRight className="ms-2" size={14} />
                                </Link>
                                <a href={APP_CONFIG.phoneLink} className="btn op-btn-outline-dark">
                                    Call {APP_CONFIG.phone}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features */}
                <div className="op-section">
                    <div className="container">
                        <div className="text-center mb-5">
                            <h2 className="op-section-title mb-3">Everything payroll needs, in one platform</h2>
                            <p className="op-section-sub mx-auto">
                                Built specifically for Ontario businesses managing one company or many.
                            </p>
                        </div>
                        <div className="row g-4">
                            {features.map((feature, idx) => (
                                <div className="col-md-6 col-lg-4" key={idx}>
                                    <div className="op-feature-card">
                                        <div className={`op-feature-icon c${(idx % 3) + 1}`}>
                                            {feature.icon}
                                        </div>
                                        <h6 className="fw-bold mb-2" style={{ color: 'var(--op-navy-dark)' }}>{feature.title}</h6>
                                        <p className="text-muted small mb-0">{feature.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* How it works */}
                <div className="op-steps op-section">
                    <div className="container">
                        <div className="text-center mb-5">
                            <h2 className="text-white fw-bold mb-2">How OrientalPay works</h2>
                            <p style={{ color: 'rgba(255,255,255,0.6)' }}>From setup to T4s, in four steps</p>
                        </div>
                        <div className="row g-4">
                            {steps.map((step) => (
                                <div className="col-6 col-lg-3" key={step.n}>
                                    <div className="op-step">
                                        <div className="op-step-n">{step.n}</div>
                                        <div className="op-step-bar" />
                                        <h6>{step.title}</h6>
                                        <p>{step.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Contact */}
                <div className="op-section">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-6">
                                <div className="op-contact-card p-4 p-md-5">
                                    <h4 className="fw-bold mb-4" style={{ color: 'var(--op-navy-dark)' }}>Get in touch</h4>

                                    <div className="op-contact-row">
                                        <div className="op-contact-icon"><FaEnvelope size={16} /></div>
                                        <div>
                                            <h6 className="fw-bold mb-1">Email</h6>
                                            <a href={APP_CONFIG.emailLink} className="text-muted small">{APP_CONFIG.email}</a>
                                        </div>
                                    </div>

                                    <div className="op-contact-row">
                                        <div className="op-contact-icon"><FaPhoneAlt size={16} /></div>
                                        <div>
                                            <h6 className="fw-bold mb-1">Phone</h6>
                                            <a href={APP_CONFIG.phoneLink} className="text-muted small">{APP_CONFIG.phone}</a>
                                        </div>
                                    </div>

                                    <div className="op-contact-row mb-0">
                                        <div className="op-contact-icon"><FaMapMarkerAlt size={16} /></div>
                                        <div>
                                            <h6 className="fw-bold mb-1">Office Location</h6>
                                            <p className="text-muted small mb-0">{APP_CONFIG.address}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
}

export default OrientalPay;